import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { McButton, McCard, McDrawer, McIcon, McInput } from '@maersk-global/mds-react-wrapper/components-core';
import {
    agentService,
    AiUsageQuotaSettings,
    AiUsageStatsResponse,
    AiUsageWindowStatsResponse,
} from '../services/agentService';
import { useNotification } from '../context/NotificationContext';
import { ConfirmationDialog } from '../components/Common/ConfirmationDialog';

const normalizeUserId = (value: string) => value.trim().toLowerCase();

export default function UserQuotaManagementPage() {
    const { showNotification } = useNotification();
    const [activeTab, setActiveTab] = useState<'users' | 'global'>('users');
    const [userIdInput, setUserIdInput] = useState('');
    const [usersLimit, setUsersLimit] = useState(200);
    const [users, setUsers] = useState<AiUsageStatsResponse[]>([]);
    const [activeUserId, setActiveUserId] = useState('');
    const [lifetimeStats, setLifetimeStats] = useState<AiUsageStatsResponse | null>(null);
    const [windowStats, setWindowStats] = useState<AiUsageWindowStatsResponse | null>(null);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
    const [settings, setSettings] = useState<AiUsageQuotaSettings | null>(null);
    const [settingsForm, setSettingsForm] = useState({
        windowSeconds: '86400',
        maxRequestsPerWindow: '500',
        maxTokensPerWindow: '200000',
        limitsEnabled: true,
    });
    const settingsFormRef = useRef(settingsForm);

    const updateSettingsForm = (
        updater:
            | Partial<typeof settingsForm>
            | ((prev: typeof settingsForm) => typeof settingsForm)
    ) => {
        const next =
            typeof updater === 'function'
                ? updater(settingsFormRef.current)
                : { ...settingsFormRef.current, ...updater };

        settingsFormRef.current = next;
        setSettingsForm(next);
    };
    const [confirmCacheResetOpen, setConfirmCacheResetOpen] = useState(false);
    const [confirmWindowResetOpen, setConfirmWindowResetOpen] = useState(false);

    const normalizedSearch = useMemo(() => normalizeUserId(userIdInput), [userIdInput]);

    const loadUsersMutation = useMutation({
        mutationFn: async (params: { search: string; limit: number }) => {
            const response = await agentService.listUserAiUsage(params.search, params.limit);
            return response.users ?? [];
        },
        onSuccess: (data) => {
            setUsers(data);
        },
        onError: (error: any) => {
            showNotification(`Failed to load users from Redis: ${error?.message || 'Unknown error'}`, 'error');
        },
    });

    const loadSettingsMutation = useMutation({
        mutationFn: () => agentService.getAiUsageQuotaSettings(),
        onSuccess: (data) => {
            setSettings(data);
            const nextForm = {
                windowSeconds: String(data.windowSeconds),
                maxRequestsPerWindow: String(data.maxRequestsPerWindow),
                maxTokensPerWindow: String(data.maxTokensPerWindow),
                limitsEnabled: data.limitsEnabled,
            };
            settingsFormRef.current = nextForm;
            setSettingsForm(nextForm);
        },
        onError: (error: any) => {
            showNotification(`Failed to load global quota settings: ${error?.message || 'Unknown error'}`, 'error');
        },
    });

    const saveSettingsMutation = useMutation({
        mutationFn: (payload: Partial<AiUsageQuotaSettings>) => agentService.updateAiUsageQuotaSettings(payload),
        onSuccess: (data) => {
            setSettings(data);
            const nextForm = {
                windowSeconds: String(data.windowSeconds),
                maxRequestsPerWindow: String(data.maxRequestsPerWindow),
                maxTokensPerWindow: String(data.maxTokensPerWindow),
                limitsEnabled: data.limitsEnabled,
            };
            settingsFormRef.current = nextForm;
            setSettingsForm(nextForm);
            showNotification('Global quota settings updated', 'success');
        },
        onError: (error: any) => {
            showNotification(`Failed to update global settings: ${error?.message || 'Unknown error'}`, 'error');
        },
    });

    const loadStatsMutation = useMutation({
        mutationFn: async (userId: string) => {
            const [lifetime, window] = await Promise.all([
                agentService.getUserAiUsage(userId),
                agentService.getUserAiUsageWindow(userId),
            ]);
            return { lifetime, window };
        },
        onMutate: () => {
            setDetailsError(null);
            setLifetimeStats(null);
            setWindowStats(null);
        },
        onSuccess: (data, userId) => {
            setActiveUserId(userId);
            setLifetimeStats(data.lifetime);
            setWindowStats(data.window);
        },
        onError: (error: any) => {
            setLifetimeStats(null);
            setWindowStats(null);
            setDetailsError(error?.message || 'Unknown error');
            showNotification(`Failed to load user quota details: ${error?.message || 'Unknown error'}`, 'error');
        },
    });

    const resetCacheMutation = useMutation({
        mutationFn: (userId: string) => agentService.resetUserAiUsageCache(userId),
        onSuccess: async (_, userId) => {
            showNotification(`Usage cache reset for ${userId}`, 'success');
            setConfirmCacheResetOpen(false);
            await loadStatsMutation.mutateAsync(userId);
            loadUsersMutation.mutate({ search: normalizedSearch, limit: usersLimit });
        },
        onError: (error: any) => {
            showNotification(`Failed to reset usage cache: ${error?.message || 'Unknown error'}`, 'error');
            setConfirmCacheResetOpen(false);
        },
    });

    const resetWindowMutation = useMutation({
        mutationFn: (userId: string) => agentService.resetUserAiUsageWindow(userId),
        onSuccess: async (_, userId) => {
            showNotification(`Current quota window reset for ${userId}`, 'success');
            setConfirmWindowResetOpen(false);
            await loadStatsMutation.mutateAsync(userId);
            loadUsersMutation.mutate({ search: normalizedSearch, limit: usersLimit });
        },
        onError: (error: any) => {
            showNotification(`Failed to reset quota window: ${error?.message || 'Unknown error'}`, 'error');
            setConfirmWindowResetOpen(false);
        },
    });

    const handleSearch = () => {
        loadUsersMutation.mutate({ search: normalizedSearch, limit: usersLimit });
    };

    const handleSelectUser = (userId: string) => {
        const normalized = normalizeUserId(userId);
        setUserIdInput(normalized);
        setActiveUserId(normalized);
        setActiveTab('users');
        setDetailsDrawerOpen(true);
        loadStatsMutation.mutate(normalized);
    };

    const handleSaveSettings = () => {
        const latestForm = settingsFormRef.current;
        const parsedWindow = Number(latestForm.windowSeconds);
        const parsedMaxRequests = Number(latestForm.maxRequestsPerWindow);
        const parsedMaxTokens = Number(latestForm.maxTokensPerWindow);

        if (!Number.isFinite(parsedWindow) || parsedWindow < 60) {
            showNotification('Window seconds must be a number >= 60', 'warning');
            return;
        }
        if (!Number.isFinite(parsedMaxRequests) || parsedMaxRequests < 1) {
            showNotification('Max requests must be a number >= 1', 'warning');
            return;
        }
        if (!Number.isFinite(parsedMaxTokens) || parsedMaxTokens < 1) {
            showNotification('Max tokens must be a number >= 1', 'warning');
            return;
        }

        saveSettingsMutation.mutate({
            limitsEnabled: latestForm.limitsEnabled,
            maxRequestsPerWindow: Math.floor(parsedMaxRequests),
            maxTokensPerWindow: Math.floor(parsedMaxTokens),
            windowSeconds: Math.floor(parsedWindow),
        });
    };

    const handleLoadMore = () => {
        const nextLimit = Math.min(usersLimit + 200, 2000);
        setUsersLimit(nextLimit);
        loadUsersMutation.mutate({ search: normalizedSearch, limit: nextLimit });
    };

    useEffect(() => {
        loadUsersMutation.mutate({ search: '', limit: usersLimit });
        loadSettingsMutation.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab !== 'users') {
            setDetailsDrawerOpen(false);
        }
    }, [activeTab]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ margin: 0 }}>User Quota Management</h2>
                    <p style={{ margin: '8px 0 0', color: 'var(--mds-color-neutral-text-secondary)' }}>
                        Super Admin view to inspect and control AI quota counters for individual users.
                    </p>
                </div>
            </div>

            <McCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <McButton
                        appearance={activeTab === 'users' ? 'primary' : 'secondary'}
                        variant={activeTab === 'users' ? 'filled' : 'outlined'}
                        onClick={() => setActiveTab('users')}
                    >
                        User Specific Quotas
                    </McButton>
                    <McButton
                        appearance={activeTab === 'global' ? 'primary' : 'secondary'}
                        variant={activeTab === 'global' ? 'filled' : 'outlined'}
                        onClick={() => setActiveTab('global')}
                    >
                        Global Settings
                    </McButton>
                </div>
            </McCard>

            {activeTab === 'global' && (
                <McCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h4 style={{ margin: 0 }}>Global Quota Settings</h4>
                    <p style={{ margin: 0, color: 'var(--mds-color-neutral-text-secondary)' }}>
                        Configure how frequently user quota resets for everyone. This updates runtime settings in Redis.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', alignItems: 'end' }}>
                        <McInput
                            label="Window Seconds"
                            value={settingsForm.windowSeconds}
                            onInput={(e: any) =>
                                updateSettingsForm({
                                    windowSeconds: String(e.detail?.value ?? e.target?.value ?? ''),
                                })
                            }
                        />
                        <McInput
                            label="Max Requests"
                            value={settingsForm.maxRequestsPerWindow}
                            onInput={(e: any) =>
                                updateSettingsForm({
                                    maxRequestsPerWindow: String(e.detail?.value ?? e.target?.value ?? ''),
                                })
                            }
                        />
                        <McInput
                            label="Max Tokens"
                            value={settingsForm.maxTokensPerWindow}
                            onInput={(e: any) =>
                                updateSettingsForm({
                                    maxTokensPerWindow: String(e.detail?.value ?? e.target?.value ?? ''),
                                })
                            }
                        />
                        <McButton
                            appearance={settingsForm.limitsEnabled ? 'secondary' : 'error'}
                            variant="outlined"
                            onClick={() =>
                                updateSettingsForm((prev) => ({
                                    ...prev,
                                    limitsEnabled: !prev.limitsEnabled,
                                }))
                            }
                        >
                            {settingsForm.limitsEnabled ? 'Limits Enabled' : 'Limits Disabled'}
                        </McButton>
                        <McButton
                            appearance="primary"
                            variant="filled"
                            onClick={handleSaveSettings}
                            disabled={saveSettingsMutation.isPending}
                        >
                            Save
                        </McButton>
                    </div>
                    {settings && (
                        <p style={{ margin: 0, color: 'var(--mds-color-neutral-text-secondary)', fontSize: '13px' }}>
                            Loaded settings: {settings.windowSeconds}s window, {settings.maxRequestsPerWindow} max requests, {settings.maxTokensPerWindow} max tokens.
                        </p>
                    )}
                </McCard>
            )}

            {activeTab === 'users' && (
                <>
                    <McCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <McInput
                                    label="Search User"
                                    value={userIdInput}
                                    placeholder="Filter by email or user ID"
                                    onInput={(e: any) => setUserIdInput(e.detail?.value ?? e.target?.value ?? '')}
                                />
                            </div>
                            <McButton
                                appearance="primary"
                                variant="filled"
                                onClick={handleSearch}
                                disabled={loadUsersMutation.isPending}
                            >
                                <McIcon icon="magnifying-glass" slot="prefix" />
                                {loadUsersMutation.isPending ? 'Loading...' : 'Filter Users'}
                            </McButton>
                            <McButton
                                appearance="secondary"
                                variant="outlined"
                                onClick={() => {
                                    setUserIdInput('');
                                    loadUsersMutation.mutate({ search: '', limit: usersLimit });
                                }}
                            >
                                Reset
                            </McButton>
                        </div>
                    </McCard>

                    <McCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ margin: 0 }}>Users from Redis ({users.length})</h4>
                            <McButton appearance="secondary" variant="outlined" onClick={handleLoadMore} disabled={usersLimit >= 2000}>
                                Load More
                            </McButton>
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(320px, 2fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) minmax(110px, auto)',
                                gap: '12px',
                                padding: '8px 12px',
                                border: '1px solid var(--mds-color-neutral-weak-decoration)',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '13px',
                            }}
                        >
                            <div>User</div>
                            <div>Requests</div>
                            <div>Tokens</div>
                            <div>Action</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                            {users.length === 0 && (
                                <div style={{ color: 'var(--mds-color-neutral-text-secondary)' }}>
                                    No user usage keys found in Redis.
                                </div>
                            )}
                            {users.map((user) => (
                                <div
                                    key={user.userId}
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(280px, 2fr) minmax(110px, 0.7fr) minmax(110px, 0.7fr) auto',
                                        gap: '12px',
                                        padding: '10px 12px',
                                        border: '1px solid var(--mds-color-neutral-weak-decoration)',
                                        borderRadius: '8px',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{user.userId}</div>
                                    <div style={{ textAlign: 'left' }}>{user.totalRequests.toLocaleString()}</div>
                                    <div style={{ textAlign: 'left' }}>{user.totalTokens.toLocaleString()}</div>
                                    <McButton
                                        appearance="secondary"
                                        variant="outlined"
                                        fit="small"
                                        onClick={() => handleSelectUser(user.userId)}
                                    >
                                        Review
                                    </McButton>
                                </div>
                            ))}
                        </div>
                    </McCard>
                </>
            )}

            <McDrawer
                open={detailsDrawerOpen}
                closed={() => setDetailsDrawerOpen(false)}
                heading={activeUserId ? `Quota Review: ${activeUserId}` : 'Quota Review'}
                width="720px"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {loadStatsMutation.isPending && (
                        <div style={{ padding: '16px', color: 'var(--mds-color-neutral-text-secondary)' }}>
                            Loading user details...
                        </div>
                    )}

                    {!loadStatsMutation.isPending && detailsError && (
                        <McCard style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ color: 'var(--mds-color-error-60)' }}>
                                    Failed to load user details: {detailsError}
                                </div>
                                <div>
                                    <McButton
                                        appearance="secondary"
                                        variant="outlined"
                                        onClick={() => activeUserId && loadStatsMutation.mutate(activeUserId)}
                                    >
                                        Retry
                                    </McButton>
                                </div>
                            </div>
                        </McCard>
                    )}

                    {!loadStatsMutation.isPending && !detailsError && activeUserId && lifetimeStats && windowStats && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(200px, 1fr))', gap: '12px' }}>
                                <StatCard title="Current Window Requests" value={windowStats.currentRequests} subtitle={`Remaining: ${windowStats.remainingRequests}`} />
                                <StatCard title="Current Window Tokens" value={windowStats.currentTokens} subtitle={`Remaining: ${windowStats.remainingTokens}`} />
                                <StatCard title="Lifetime Requests" value={lifetimeStats.totalRequests} subtitle="All-time aggregate" />
                                <StatCard title="Lifetime Tokens" value={lifetimeStats.totalTokens} subtitle="All-time aggregate" />
                            </div>

                            <McCard style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <h4 style={{ margin: 0 }}>Quota Window Details</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <LabelValue label="User" value={windowStats.userId} />
                                    <LabelValue label="Window Seconds" value={windowStats.windowSeconds} />
                                    <LabelValue label="Window Start" value={new Date(windowStats.windowStartEpochMs).toLocaleString()} />
                                    <LabelValue label="Window Reset" value={new Date(windowStats.windowResetEpochMs).toLocaleString()} />
                                    <LabelValue label="Max Requests" value={windowStats.maxRequestsPerWindow} />
                                    <LabelValue label="Max Tokens" value={windowStats.maxTokensPerWindow} />
                                </div>
                            </McCard>

                            <McCard style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <h4 style={{ margin: 0 }}>Admin Actions</h4>
                                <p style={{ margin: 0, color: 'var(--mds-color-neutral-text-secondary)' }}>
                                    Reset operations only clear cached usage counters; they do not delete historical records.
                                </p>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                    <McButton
                                        appearance="error"
                                        variant="outlined"
                                        onClick={() => setConfirmCacheResetOpen(true)}
                                        disabled={resetCacheMutation.isPending || loadStatsMutation.isPending}
                                    >
                                        Reset User Usage
                                    </McButton>
                                    <McButton
                                        appearance="error"
                                        variant="outlined"
                                        onClick={() => setConfirmWindowResetOpen(true)}
                                        disabled={resetWindowMutation.isPending || loadStatsMutation.isPending}
                                    >
                                        Reset Current Window
                                    </McButton>
                                    <McButton
                                        appearance="secondary"
                                        variant="outlined"
                                        onClick={() => loadStatsMutation.mutate(activeUserId)}
                                        disabled={loadStatsMutation.isPending}
                                    >
                                        <McIcon icon="rotate" slot="prefix" />
                                        Refresh
                                    </McButton>
                                </div>
                            </McCard>
                        </>
                    )}
                </div>
            </McDrawer>

            <ConfirmationDialog
                open={confirmCacheResetOpen}
                onClose={() => setConfirmCacheResetOpen(false)}
                onConfirm={() => resetCacheMutation.mutate(activeUserId)}
                title="Reset Usage Cache"
                content={<p>Reset usage state for {activeUserId}? This clears Redis, pending buffers, and persisted totals for this user.</p>}
                confirmLabel="Reset Usage"
                variant="danger"
                loading={resetCacheMutation.isPending}
            />

            <ConfirmationDialog
                open={confirmWindowResetOpen}
                onClose={() => setConfirmWindowResetOpen(false)}
                onConfirm={() => resetWindowMutation.mutate(activeUserId)}
                title="Reset Current Window"
                content={<p>Reset current quota window for {activeUserId}? This effectively unlocks the user for the active window.</p>}
                confirmLabel="Reset Window"
                variant="danger"
                loading={resetWindowMutation.isPending}
            />
        </div>
    );
}

function StatCard({ title, value, subtitle }: { title: string; value: number | string; subtitle?: string }) {
    return (
        <McCard style={{ padding: '16px', minHeight: '108px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ color: 'var(--mds-color-neutral-text-secondary)', fontSize: '12px', lineHeight: 1.3 }}>{title}</div>
                <div style={{ fontSize: '34px', fontWeight: 700, lineHeight: 1.1 }}>
                    {typeof value === 'number' ? value.toLocaleString() : value}
                </div>
                {subtitle && (
                    <div style={{ color: 'var(--mds-color-neutral-text-secondary)', fontSize: '12px', lineHeight: 1.3 }}>
                        {subtitle}
                    </div>
                )}
            </div>
        </McCard>
    );
}

function LabelValue({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ color: 'var(--mds-color-neutral-text-secondary)', fontSize: '12px' }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{value}</span>
        </div>
    );
}
